import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo} from 'react';
import type {AssetSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  ActiveInactivePills,
  AssetTypePills,
} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {
  ActiveStateDataOptions,
  AssetTypeDataOptions,
} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useResetOnScopeChange} from 'src/app/tree-browser/useResetOnScopeChange';
import {useGetAssetListV2Query} from 'src/redux/queries/asset';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface AssetsTableProps {
  parentId?: number;
}

export const AssetsTable: React.FC<AssetsTableProps> = ({parentId}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<AssetSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'isLeaf',
        header: 'Asset Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: AssetTypeDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...AssetTypePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'project.name',
        header: 'Project',
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assets',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  useResetOnScopeChange(parentId, () =>
    tableState.setPagination(current => ({...current, pageIndex: 0}))
  );

  const {start, length, filters, filterFns, sorting} = tableState;

  const {currentData, isLoading, isError, isFetching, refetch} =
    useGetAssetListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        parentId: parentId,
      },
      {refetchOnMountOrArgChange: true}
    );
  const rows = currentData?.data ?? [];
  const totalCount = currentData?.totalRecords ?? 0;

  const handleAddClick = () =>
    parentId
      ? navigate(PathUtility.AssetCreate(String(parentId)))
      : navigate(PathUtility.AssetCreate());

  const handleNavigateToAsset = (row: AssetSimpleResponse) =>
    navigate(PathUtility.AssetView(row.id, 'overview'));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToAsset(row.original)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Assets}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action variant="create" onClick={handleAddClick}>
                New Asset
              </TableToolbar.Action>
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return table;
};
